import { IActionDefinitionDto, ICRUDFormDiMetadata, IImportMappedExcelSetupWizardConfig } from "@kuika/kuika-cl-model";
import { isWorkflowCollabEnabled } from "../../../services/collab/collab-feature-gate";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface WizardProviderState {
  isElementPickerWithActionOpen: boolean;
  setIsElementPickerWithActionOpen: (value: boolean) => void;
  actionDefinitionForCreateElementForAction: IActionDefinitionDto | null;
  setActionDefinitionForCreateElementForAction: (action: IActionDefinitionDto | null) => void;
  targetKuikaIdForCreateElementForAction: string | null;
  setTargetKuikaIdForCreateElementForAction: (targetKuikaId: string | null) => void;
  processIdForWizardForBusinessObject: string | null;
  setProcessIdForWizardForBusinessObject: (processId: string | null) => void;
  isWizardForFormOpen: boolean;
  setIsWizardForFormOpen: (value: boolean) => void;
  targetKuikaIdForFormWizard: string | null;
  setTargetKuikaIdForFormWizard: (targetKuikaId: string | null) => void;
  metadataForFormWizard: ICRUDFormDiMetadata | null;
  setMetadataForFormWizard: (metadata: ICRUDFormDiMetadata | null, cb?: () => void) => void;
  isWorkflowScreenStepWizardOpen: boolean;
  setIsWorkflowScreenStepWizardOpen: (value: boolean, processId?: string, stepId?: string) => void;
  workflowScreenStepWizardInitialProcessId: string | null;
  workflowScreenStepWizardInitialStepId: string | null;
  isPickProcessModalOpen: boolean;
  setIsPickProcessModalOpen: (value: boolean) => void;
  isImportMappedExcelSetupWizardOpen: boolean;
  setImportMappedExcelSetupWizardOpen: (value: boolean, config: IImportMappedExcelSetupWizardConfig | null) => void;
  importMappedExcelSetupWizardConfig: IImportMappedExcelSetupWizardConfig | null;
}

export class WizardProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  WizardProviderState
> {
  static defaultState: WizardProviderState = {
    isElementPickerWithActionOpen: false,
    setIsElementPickerWithActionOpen: () => {},
    actionDefinitionForCreateElementForAction: null,
    setActionDefinitionForCreateElementForAction: () => {},
    targetKuikaIdForCreateElementForAction: null,
    setTargetKuikaIdForCreateElementForAction: () => {},
    processIdForWizardForBusinessObject: null,
    setProcessIdForWizardForBusinessObject: () => {},
    isWizardForFormOpen: false,
    setIsWizardForFormOpen: () => false,
    targetKuikaIdForFormWizard: null,
    setTargetKuikaIdForFormWizard: () => {},
    metadataForFormWizard: null,
    setMetadataForFormWizard: () => {},
    isWorkflowScreenStepWizardOpen: false,
    setIsWorkflowScreenStepWizardOpen: () => {},
    workflowScreenStepWizardInitialProcessId: null,
    workflowScreenStepWizardInitialStepId: null,
    isPickProcessModalOpen: false,
    setIsPickProcessModalOpen: () => {},
    isImportMappedExcelSetupWizardOpen: false,
    setImportMappedExcelSetupWizardOpen: () => {},
    importMappedExcelSetupWizardConfig: null
  };

  getProviderValues = (): WizardProviderState => {
    return {
      isElementPickerWithActionOpen: this.state.isElementPickerWithActionOpen,
      setIsElementPickerWithActionOpen: this.setIsElementPickerWithActionOpen,
      actionDefinitionForCreateElementForAction: this.state.actionDefinitionForCreateElementForAction,
      setActionDefinitionForCreateElementForAction: this.setActionDefinitionForCreateElementForAction,
      targetKuikaIdForCreateElementForAction: this.state.targetKuikaIdForCreateElementForAction,
      setTargetKuikaIdForCreateElementForAction: this.setTargetKuikaIdForCreateElementForAction,
      processIdForWizardForBusinessObject: this.state.processIdForWizardForBusinessObject,
      setProcessIdForWizardForBusinessObject: this.setProcessIdForWizardForBusinessObject,
      isWizardForFormOpen: this.state.isWizardForFormOpen,
      setIsWizardForFormOpen: this.setIsWizardForFormOpen,
      targetKuikaIdForFormWizard: this.state.targetKuikaIdForFormWizard,
      setTargetKuikaIdForFormWizard: this.setTargetKuikaIdForFormWizard,
      metadataForFormWizard: this.state.metadataForFormWizard,
      setMetadataForFormWizard: this.setMetadataForFormWizard,
      isWorkflowScreenStepWizardOpen: this.state.isWorkflowScreenStepWizardOpen,
      setIsWorkflowScreenStepWizardOpen: this.setIsWorkflowScreenStepWizardOpen,
      workflowScreenStepWizardInitialProcessId: this.state.workflowScreenStepWizardInitialProcessId,
      workflowScreenStepWizardInitialStepId: this.state.workflowScreenStepWizardInitialStepId,
      isPickProcessModalOpen: this.state.isPickProcessModalOpen,
      setIsPickProcessModalOpen: this.setIsPickProcessModalOpen,
      isImportMappedExcelSetupWizardOpen: this.state.isImportMappedExcelSetupWizardOpen,
      setImportMappedExcelSetupWizardOpen: this.setImportMappedExcelSetupWizardOpen,
      importMappedExcelSetupWizardConfig: this.state.importMappedExcelSetupWizardConfig
    };
  };

  setImportMappedExcelSetupWizardOpen = (value: boolean, config: IImportMappedExcelSetupWizardConfig | null) => {
    if (value) {
      if (config === null) {
        throw new Error("config cannot be null when opening import mapped excel setup wizard");
      }

      this.setState({ isImportMappedExcelSetupWizardOpen: value, importMappedExcelSetupWizardConfig: config });
    } else {
      this.setState({ isImportMappedExcelSetupWizardOpen: value, importMappedExcelSetupWizardConfig: null });
    }
  };

  setIsPickProcessModalOpen = (value: boolean) => {
    this.setState({ isPickProcessModalOpen: value });
  };

  setIsWorkflowScreenStepWizardOpen = (value: boolean, processId?: string, stepId?: string) => {
    if (value) {
      this.setState(
        {
          isWorkflowScreenStepWizardOpen: value,
          workflowScreenStepWizardInitialProcessId: processId || null,
          workflowScreenStepWizardInitialStepId: stepId || null
        },
        () => {
          // Belt-and-suspenders vs a missed designer-live event: the wizard reads its
          // linked-screen view from project.processStepScreenIds + screenList, which
          // another user's SERVER-SIDE screen creation mutates. Refresh both silently
          // on open so the modal never opens on a stale mapping (collab flag only —
          // legacy single-user behavior is byte-identical).
          if (isWorkflowCollabEnabled()) {
            const projectId: string = this.partial.projectProvider.getProject()?.id ?? "";
            void this.partial.projectProvider.refreshProjectFromServer(projectId);
            void this.partial.screenProvider.setScreenList();
          }
        }
      );
    } else {
      this.setState(
        {
          isWorkflowScreenStepWizardOpen: value,
          workflowScreenStepWizardInitialProcessId: null,
          workflowScreenStepWizardInitialStepId: null
        },
        () => {
          // The workflowScreens.changed hybrid refresh defers while the wizard is open —
          // closing it is the "tool went idle" moment that applies the pending reload.
          notifyDesignerLiveToolIdle();
        }
      );
    }
  };

  setMetadataForFormWizard = (metadata: ICRUDFormDiMetadata | null, cb?: () => void) => {
    this.setState({ metadataForFormWizard: metadata }, cb);
  };

  setIsWizardForFormOpen = (value: boolean) => {
    this.setState({ isWizardForFormOpen: value });
  };

  setTargetKuikaIdForFormWizard = (targetKuikaId: string | null) => {
    this.setState({ targetKuikaIdForFormWizard: targetKuikaId });
  };

  setProcessIdForWizardForBusinessObject = (processId: string | null) => {
    this.setState({ processIdForWizardForBusinessObject: processId });
  };

  setTargetKuikaIdForWizardForBusinessObject = (targetKuikaId: string | null) => {
    this.setState({ targetKuikaIdForWizardForBusinessObject: targetKuikaId });
  };

  setTargetKuikaIdForCreateElementForAction = (targetKuikaId: string | null) => {
    this.setState({ targetKuikaIdForCreateElementForAction: targetKuikaId });
  };

  setActionDefinitionForCreateElementForAction = (actionDefinition: IActionDefinitionDto | null) => {
    this.setState({ actionDefinitionForCreateElementForAction: actionDefinition });
  };

  setIsElementPickerWithActionOpen = (value: boolean) => {
    this.setState({ isElementPickerWithActionOpen: value });
  };
}
